import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import LanguageSelector from "../../components/LanguageSelector";

type SectionCard = {
  id: string;
  title: string;
  subtitle: string; // keep non-clickable text as-is
  bullets: { key: string; label: string }[];
  to?: string;
};

const HelpSupportStart: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showLanguage, setShowLanguage] = useState(false);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Bullet key -> anchor id
const accountAnchorMap: Record<string, string> = {
  accountOverview: "whatIsMyAccount",
  registration: "registerNewAccount",
  login: "login",
  myProfile: "findingMyProfile",
  settings: "settingsOverview",
  dataPrivacy: "dataPrivacyOverview",
  security: "securityOverview",
  profileManagement: "profileManagementOverview",
  support: "contactSupport",
};

const platformAnchorMap: Record<string, string> = {
  platformOverview: "platformOverview",
  feeds: "feeds",
  search: "search",
  content: "content",
  chat: "chat",
  notification: "notification",
  myProfile: "myProfile",
  safety: "safety",
  troubleshooting: "troubleshooting",
};

const adAnchorMap: Record<string, string> = {
  adGuide: "advertisingOverview",
  campaignCreation: "createCampaign",
  targetingDelivery: "targetLocation",
  reviewModeration: "adReview",
  performanceBilling: "pricingCpm",
  bestPractices: "creativeBestPractices",
  compliance: "prohibitedContent",
  troubleshooting: "invalidUrl",
};

const faqAnchorMap: Record<string, string> = {
  feedsPosting: "what-is-gallery",
  locationPrivacy: "location-needed",
  chatEncryption: "is-chat-e2ee",
  accountSecurity: "forgot-password",
  followingLikes: "like-unlike",
  safetyReporting: "when-block",
  adsBilling: "ads-required",
  troubleshooting: "app-crashes",
};

  const sections: SectionCard[] = [
    {
      id: "account",
      title: t("help.start.sections.account.title", "Account Guide"),
      subtitle: t(
        "help.start.sections.account.subtitle",
        "Guide for registration, login & settings."
      ),
      to: "/help/account",
 bullets: [
  { key: "accountOverview", label: t("accountSupport.sidebar.accountOverview") },
  { key: "registration", label: t("accountSupport.sidebar.registration") },
  { key: "login", label: t("accountSupport.sidebar.login") },
  { key: "myProfile", label: t("accountSupport.sidebar.myProfile") },
  { key: "settings", label: t("accountSupport.sidebar.settings") },
  { key: "dataPrivacy", label: t("accountSupport.sidebar.dataPrivacy") },
  { key: "security", label: t("accountSupport.sidebar.security") },
  { key: "profileManagement", label: t("accountSupport.sidebar.profileManagement") },
  { key: "support", label: t("accountSupport.sidebar.support") },
],
    },
    {
      id: "platform",
      title: t("help.start.sections.platform.title", "Platform Guide"),
      subtitle: t(
        "help.start.sections.platform.subtitle",
        "How Elnydo works"
      ),
      to: "/help/platform",    
      bullets: [
  { key: "platformOverview", label: t("platformGuide.sidebar.platformOverview") },
  { key: "feeds", label: t("platformGuide.sidebar.feeds") },
  { key: "search", label: t("platformGuide.sidebar.search") },
  { key: "content", label: t("platformGuide.sidebar.content") },
  { key: "chat", label: t("platformGuide.sidebar.chat") },
  { key: "notification", label: t("platformGuide.sidebar.notification") },
  { key: "myProfile", label: t("platformGuide.sidebar.myProfile") },
  { key: "safety", label: t("platformGuide.sidebar.safety") },
  { key: "troubleshooting", label: t("platformGuide.sidebar.troubleshooting") },
],
    },
    {
      id: "ads",
      title: t("help.start.sections.ads.title", "Advertisement Guide"),
      subtitle: t(
        "help.start.sections.ads.subtitle",
        "Creating ads, targeting, budgets, review rules, and billing."
      ),
      to: "/help/advertisement",
      bullets: [
  { key: "adGuide", label: t("advertisementGuide.sidebar.adGuide") },
  { key: "campaignCreation", label: t("advertisementGuide.sidebar.campaignCreation") },
  { key: "targetingDelivery", label: t("advertisementGuide.sidebar.targetingDelivery") },
  { key: "reviewModeration", label: t("advertisementGuide.sidebar.reviewModeration") },
  { key: "performanceBilling", label: t("advertisementGuide.sidebar.performanceBilling") },
  { key: "bestPractices", label: t("advertisementGuide.sidebar.bestPractices") },
  { key: "compliance", label: t("advertisementGuide.sidebar.compliance") },
  { key: "troubleshooting", label: t("advertisementGuide.sidebar.troubleshooting") },
],
    },
   {
  id: "faq",
  title: t("help.start.sections.faq.title", "FAQ"),
  subtitle: t(
    "help.start.sections.faq.subtitle",
    "Quick answers — grouped by topic so users can find things fast."
  ),
  to: "/help/faq",
  bullets: [
    { key: "feedsPosting", label: t("faq.sidebar.feedsPosting") },
    { key: "locationPrivacy", label: t("faq.sidebar.locationPrivacy") },
    { key: "chatEncryption", label: t("faq.sidebar.chatEncryption") },
    { key: "accountSecurity", label: t("faq.sidebar.accountSecurity") },
    { key: "followingLikes", label: t("faq.sidebar.followingLikes") },
    { key: "safetyReporting", label: t("faq.sidebar.safetyReporting") },
    { key: "adsBilling", label: t("faq.sidebar.adsBilling") },
    { key: "troubleshooting", label: t("faq.sidebar.troubleshooting") },
  ],
},
  ];

  const goSection = (to?: string) => {
    if (!to) return;
    navigate(to);
  };

  const goBullet = (section: SectionCard, bulletKey: string) => {
    if (!section.to) return;

    if (section.id === "platform") {
      const anchor = platformAnchorMap[bulletKey];
      navigate(anchor ? `${section.to}#${anchor}` : section.to);
      return;
    }

    if (section.id === "account") {
      const anchor = accountAnchorMap[bulletKey];
      navigate(anchor ? `${section.to}#${anchor}` : section.to);
      return;
    }

    if (section.id === "ads") {
      const anchor = adAnchorMap[bulletKey];
      navigate(anchor ? `${section.to}#${anchor}` : section.to);
      return;
    }

    if (section.id === "faq") {
      const anchor = faqAnchorMap[bulletKey];
      navigate(anchor ? `${section.to}#${anchor}` : section.to);
      return;
    }

    navigate(section.to);
  };

  return (
    <div style={shell}>
      {/* Top bar */}
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
  <HighLogo
    src={logo}
    style={{ width: 170, display: "block" }}
    padding={0}
  />
</div>

       <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 10 }}>
  <button
    type="button"
    onClick={() => navigate(-1)}
    style={ghostBtn}
    aria-label={t("platformGuide.meta.back", "Back")}
    title={t("platformGuide.meta.back", "Back")}
  >
    ← {t("platformGuide.meta.back", "Back")}
  </button>

  <button
    type="button"
    onClick={() => setShowLanguage(true)}
    style={ghostBtn}
  >
    🌐 {t("home.footer.language", "Language")}
  </button>
</div>
      </header>

      <main style={main}>
        <div style={pageIntro}>
          <h1 style={h1}>{t("help.start.title", "Help & Support")}</h1>
          <p style={subhead}>
            {t("help.start.subtitle", "Guides, FAQs, and support contact. Pick a section below.")}
          </p>
        </div>

        {/* Cards */}
        <section style={tableWrap}>
          <div style={tableGrid}>
            {sections.map((s) => (
              <div
                key={s.id}
                style={card}
                role={s.to ? "button" : undefined}
                tabIndex={s.to ? 0 : -1}
                onClick={() => goSection(s.to)}
                onKeyDown={(e) => {
                  if (!s.to) return;
                  if (e.key === "Enter" || e.key === " ") goSection(s.to);
                }}
                aria-label={s.to ? `${t("help.start.ui.open", "Open")} ${s.title}` : s.title}
              >
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goSection(s.to);
                    }}
                    style={titleBtn}
                    className="hs-link"
                  >
                    {s.title}
                  </button>

                  <div style={subtitleText}>{s.subtitle}</div>
                </div>

                <ul style={bulletList}>
                  {s.bullets.slice(0, 12).map((b, idx) => (
                    <li key={b.key ?? idx} style={bulletItem}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goBullet(s, b.key);
                        }}
                        style={bulletBtn}
                        className="hs-link"
                        aria-label={`${s.title}: ${b.label}`}
                        title={b.label}
                      >
                        <span style={bulletMark}>•</span>
                        <span style={{ minWidth: 0 }}>{b.label}</span>
                      </button>
                    </li>
                  ))}

                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section style={contactCta}>
          <div>
            <div style={sectionTitle}>{t("help.start.contact.title", "Contact Support")}</div>
            <div style={sectionHint}>
              {t("help.start.contact.subtitle", "If you need additional help, click here to contact us.")}
            </div>
          </div>
          <button type="button" onClick={() => navigate("/help/contact")} style={ctaBtn}>
            {t("help.start.contact.cta", "Contact")} →
          </button>
        </section>
      </main>

      {showLanguage && (
  <div style={languageOverlay} onClick={() => setShowLanguage(false)}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={languageModalWrap}
    >
      <LanguageSelector
        onClose={() => setShowLanguage(false)}
        offsetY={0}
      />
    </div>
  </div>
)}

      <style>{`
        .hs-link { transition: color 140ms ease, opacity 140ms ease; }
        .hs-link:hover { color: #e5e7eb !important; opacity: 1 !important; cursor: pointer; }
        .hs-link:focus-visible { outline: 2px solid rgba(163,230,53,0.6); outline-offset: 3px; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default HelpSupportStart;

/* ----------------- styles ----------------- */

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginLeft: "-28px",
  marginTop: '5px',
};

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid #4b5563",
  background: "transparent",
  color: "#e5e7eb",
  cursor: "pointer",
  fontSize: 13,
};

const main: React.CSSProperties = {
  padding: "26px 7vw 70px",
  overflow: "hidden",
  overscrollBehavior: "none",
  boxSizing: "border-box",
};

const pageIntro: React.CSSProperties = {
  maxWidth: 1400,
  margin: "0 auto 18px",
};

const h1: React.CSSProperties = {
  marginLeft: 0,
  marginTop: 0,
  fontSize: 24,
  letterSpacing: -0.03,
  lineHeight: 1.1,
  color: "#84cc16",
  fontWeight: 800,
};

const subhead: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#9ca3af",
  fontSize: 14,
  marginLeft: 0,
};

const tableWrap: React.CSSProperties = {
  maxWidth: 1400,
  margin: "18px auto 26px",
  marginTop: 30,
};

const tableGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(280px, 1fr))",
  gap: 16,
  overflowX: "hidden",
};

const card: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(3,7,18,1))",
  boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
  padding: 16,
  minHeight: 320,
  position: "relative",
  overflow: "hidden",
  outline: "none",
};

const titleBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: -0.02,
  color: "#cbd5e1",
  cursor: "pointer",
};

const subtitleText: React.CSSProperties = {
  marginTop: 15,
  fontSize: 14,
  fontWeight: 600,
  color: "#94a3b8",
  lineHeight: 1.35,
  cursor: "default",
};

const bulletList: React.CSSProperties = {
  margin: "12px 0 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 8,
};

const bulletItem: React.CSSProperties = {
  margin: 0,
  padding: 0,
};

const bulletBtn: React.CSSProperties = {
  width: "100%",
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  color: "#cbd5e1",
  fontSize: 14,
  lineHeight: 1.35,
  cursor: "pointer",
  marginTop: 3,
};

const bulletMark: React.CSSProperties = {
  color: "rgba(148,163,184,0.85)",
  flex: "0 0 auto",
  marginTop: 1,
};

const sectionTitle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: -0.02,
  color: "#e5e7eb",
};

const sectionHint: React.CSSProperties = {
  marginTop: 6,
  fontSize: 14,
  color: "#9ca3af",
  lineHeight: 1.35,
  maxWidth: 760,
};

const contactCta: React.CSSProperties = {
  maxWidth: 1400,
  margin: "0 auto",
  borderRadius: 24,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.85), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  padding: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
};

const ctaBtn: React.CSSProperties = {
  padding: "10px 16px",
  borderRadius: 999,
  border: "none",
  background: "linear-gradient(135deg,#22c55e,#a3e635)",
  color: "#020617",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const languageOverlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  backgroundColor: "rgba(0,0,0,0.78)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const languageModalWrap: React.CSSProperties = {
  transform: "translateY(5px)",
};